import { createStyles, ScrollArea } from '@mantine/core';
import * as Input from '@observablehq/inputs';
import { getTextWidth } from '@repo/foundations/components/Skeleton/util';
import { typography } from '@repo/theme/primitives';
import dayjs from 'dayjs';
import { useCallback, useMemo, useRef, useState } from 'react';
import { arrayToObjects } from '../../utils/metrics';
import { Wrapper } from './components/Wrapper';

const useStyles = createStyles(
	(
		theme,
		{
			width,
			height,
			resizable,
		}: { width?: string; height?: string; resizable?: boolean }
	) => ({
		wrapper: {
			form: {
				width,
				maxHeight: height ? `${height} !important` : undefined,
				display: 'block',
				overflowY: 'auto',
				color: theme.other.getColor('text/primary/default'),
			},
			'form table': {
				width: '100%',
				borderCollapse: 'collapse',
				...(resizable && { tableLayout: 'fixed' }),
			},
			// Only hide first column when not resizable (for svg table)
			...(!resizable && {
				'form table thead tr th:first-child': {
					display: 'none',
				},
				'form table tr td:first-child': {
					display: 'none',
				},
			}),
			'form table thead tr th': {
				fontWeight: typography.weight.bold,
				textAlign: 'center',
				fontSize: 14,
				border: `solid 1px ${theme.other.getColor('border/secondary/default')}`,
				textOverflow: 'ellipsis',
				padding: '3px 6.5px 3px 0',
				textWrap: 'nowrap',
				...(resizable && {
					position: 'relative',
					minWidth: '100px',
					maxWidth: 'none',
					resize: 'horizontal',
					overflow: 'visible',
				}),
			},
			...(resizable && {
				'form table thead tr th:last-child': {
					resize: 'none',
				},
				'form table thead tr th:hover': {
					cursor: 'col-resize',
				},
				'form table thead tr th::after': {
					content: '""',
					position: 'absolute',
					right: 0,
					top: 0,
					bottom: 0,
					width: '4px',
					cursor: 'col-resize',
					background: 'transparent',
				},
				'form table thead tr th:hover::after': {
					background: theme.other.getColor('border/secondary/hover'),
				},
			}),
			'form table tr td': {
				fontWeight: typography.weight.regular,
				fontSize: 14,
				textAlign: 'center',
				border: `solid 1px ${theme.other.getColor('border/secondary/default')}`,
				textOverflow: 'ellipsis',
				overflow: 'hidden',
				padding: '3px 6.5px 3px 0',
			},
		},
	})
);

const isTimestamp = (value: string): boolean => {
	if (!value) return false;

	// format: 2022-06-10 21:23:37.946000+00:00
	const timestampRegex =
		/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(\.\d+)?([+-]\d{2}:\d{2})?$/;

	if (timestampRegex.test(value)) {
		try {
			const date = new Date(value);
			return !isNaN(date.getTime());
		} catch {
			return false;
		}
	}

	return false;
};

const formatTimestamp = (value: string): string => {
	try {
		return dayjs(value).format('MMMM D, YYYY h:mm A Z');
	} catch (e) {
		return value;
	}
};

const processValue = (value: any): any => {
	if (isTimestamp(value)) {
		return formatTimestamp(value);
	}
	if (typeof value === 'boolean') {
		return value.toString();
	}
	return value;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const svgTable = (results: any[][]) => {
	const data = results;

	const processedData = data.map((row) =>
		row.map((cell) => processValue(cell))
	);

	const formattedData = arrayToObjects(processedData);

	return Input.table(formattedData, {
		multiple: false,
		fixedWidth: true,
	});
};

interface DataDisplayTableProps {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	results: any[][];
	height?: string;
	width?: string;
	resizable?: boolean;
}

const useResizeColumns = (resizable: boolean, results: any[][]) => {
	const [columnWidths, setColumnWidths] = useState<number[]>([]);
	const [clickXPosition, setClickXPosition] = useState(Infinity);
	const [resizingColumnIndex, setResizingColumnIndex] = useState<number | null>(
		null
	);
	const tableRef = useRef<HTMLTableElement>(null);
	const eventListenerAdded = useRef(false);
	const lastResultsHash = useRef('');
	const hasInitialized = useRef(false);
	const cursorStyleRef = useRef<HTMLStyleElement | null>(null);

	// Compute column widths using useMemo
	const computedColumnWidths = useMemo(() => {
		if (results.length > 1 && resizable) {
			// Calculate column widths based on content length using getTextWidth
			const widths = results[0].map((header, index) => {
				const headerText = String(header);
				// Use getTextWidth for measure header text width
				const headerWidth =
					getTextWidth(headerText, {
						fontSize: '14px',
						fontWeight: 'bold',
					}) || headerText.length * 12;

				// Add padding and border width
				const baseWidth = Math.max(100, Math.min(300, headerWidth + 20));

				// Check if there are data rows to consider their content too
				const maxDataWidth = Math.max(
					...results.slice(1).map((row) => {
						const cellText = String(row[index] || '');
						return (
							getTextWidth(cellText, {
								fontSize: '14px',
								fontWeight: 'normal',
							}) || cellText.length * 10
						);
					})
				);
				const dataBasedWidth = Math.max(100, Math.min(300, maxDataWidth + 20));
				return Math.max(baseWidth, dataBasedWidth);
			});

			return widths;
		}
		return [];
	}, [results, resizable]);

	// Create a hash of the results to detect changes
	const resultsHash = useMemo(() => JSON.stringify(results), [results]);

	// Initialize column widths on first render or when results change
	if (!hasInitialized.current && computedColumnWidths.length > 0) {
		hasInitialized.current = true;
		lastResultsHash.current = resultsHash;
		setColumnWidths(computedColumnWidths);
	} else if (
		hasInitialized.current &&
		resultsHash !== lastResultsHash.current &&
		computedColumnWidths.length > 0
	) {
		lastResultsHash.current = resultsHash;
		setColumnWidths(computedColumnWidths);
	}

	// Handle cursor style changes using refs to avoid DOM manipulation during render
	const shouldShowCursor = clickXPosition !== Infinity;

	// Use a callback to manage cursor style after render
	const updateCursorStyle = useCallback(() => {
		if (shouldShowCursor && !cursorStyleRef.current) {
			const style = document.createElement('style');
			style.innerHTML = '*{cursor: col-resize!important;}';
			style.id = 'cursor-style';
			document.head.appendChild(style);
			cursorStyleRef.current = style;
		} else if (!shouldShowCursor && cursorStyleRef.current) {
			cursorStyleRef.current.remove();
			cursorStyleRef.current = null;
		}
	}, [shouldShowCursor]);

	// Schedule cursor style update after render
	if (typeof window !== 'undefined') {
		// Use a microtask to ensure this runs after the current render cycle
		Promise.resolve().then(updateCursorStyle);
	}

	// Create a stable function reference using useRef
	const handleMouseUpRef = useRef<(e: MouseEvent) => void>();

	// Update the ref with the current function
	handleMouseUpRef.current = (e: MouseEvent) => {
		e.preventDefault();

		if (resizingColumnIndex === null || !tableRef.current) {
			return;
		}

		const headerCell = tableRef.current.querySelector(
			`th:nth-child(${resizingColumnIndex + 1})`
		) as HTMLElement;
		if (!headerCell) return;

		const widthChange = e.clientX - clickXPosition;
		const newWidth = Math.max(100, headerCell.offsetWidth + widthChange);

		setColumnWidths((prev) => {
			const newWidths = [...prev];
			newWidths[resizingColumnIndex] = newWidth;
			return newWidths;
		});

		setClickXPosition(Infinity);
		setResizingColumnIndex(null);
		document.body.style.cursor = 'auto';
	};

	const handleMouseDown = useCallback(
		(e: React.MouseEvent, columnIndex: number) => {
			e.preventDefault();
			e.stopPropagation();
			setClickXPosition(e.clientX);
			setResizingColumnIndex(columnIndex);
			document.body.style.cursor = 'col-resize';
		},
		[]
	);

	// Add event listener when resizing starts
	if (clickXPosition !== Infinity && !eventListenerAdded.current) {
		document.addEventListener('mouseup', handleMouseUpRef.current);
		eventListenerAdded.current = true;
	}

	// Remove event listener when resizing ends
	if (clickXPosition === Infinity && eventListenerAdded.current) {
		document.removeEventListener('mouseup', handleMouseUpRef.current);
		eventListenerAdded.current = false;
	}

	return {
		columnWidths,
		tableRef,
		handleMouseDown,
		resizingColumnIndex,
	};
};

export function DataDisplayTable({
	height = '400px',
	width = '100%',
	results,
	resizable = false,
}: DataDisplayTableProps) {
	const { classes } = useStyles({ width, height, resizable });
	const { columnWidths, tableRef, handleMouseDown } = useResizeColumns(
		resizable,
		results
	);

	const getColumnStyle = (index: number) => {
		if (!resizable || columnWidths.length === 0) return {};
		return {
			width: `${columnWidths[index]}px`,
			minWidth: '100px',
		};
	};

	if (resizable && results.length > 1) {
		const headers = results[0];
		const data = results.slice(1);

		return (
			<div className={classes.wrapper}>
				<ScrollArea type="scroll" className={classes.wrapper}>
					<form>
						<table ref={tableRef}>
							<thead>
								<tr>
									{headers.map((header, index) => (
										<th
											key={index}
											style={getColumnStyle(index)}
											onMouseDown={(e) => handleMouseDown(e, index)}
										>
											{processValue(header)}
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								{data.map((row, rowIndex) => (
									<tr key={rowIndex}>
										{row.map((cell, cellIndex) => (
											<td key={cellIndex} style={getColumnStyle(cellIndex)}>
												{processValue(cell)}
											</td>
										))}
									</tr>
								))}
							</tbody>
						</table>
					</form>
				</ScrollArea>
			</div>
		);
	}

	return <Wrapper svgElement={svgTable(results)} className={classes.wrapper} />;
}
